import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import SEO from "@/components/seo/SEO";
import { AmbientOrbs, Eyebrow, GoldButton, GhostButton } from "@/components/common/ui";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterConfirm() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") || "";
  const [state, setState] = useState({ status: "loading", email: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "not_found" });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/newsletter/confirm?token=${encodeURIComponent(token)}`);
        const data = await r.json();
        setState(data);
      } catch (e) {
        setState({ status: "error" });
      }
    })();
  }, [token]);

  const copy = {
    loading: {
      title: isNl ? "Bezig met bevestigen…" : "Confirming your subscription…",
      body: isNl ? "Even geduld." : "One moment please.",
    },
    confirmed: {
      title: isNl ? "Welkom binnen." : "Welcome in.",
      body: isNl
        ? "Uw abonnement op Notes for hoteliers is bevestigd. U ontvangt maandelijks een compacte selectie hospitality-notes."
        : "Your subscription to Notes for hoteliers is confirmed. You'll receive a tightly-curated monthly dispatch.",
    },
    already_confirmed: {
      title: isNl ? "U bent al bevestigd." : "You're already confirmed.",
      body: isNl ? "Niets meer te doen — u staat op de lijst." : "Nothing else to do — you're on the list.",
    },
    not_found: {
      title: isNl ? "Deze bevestigingslink is niet geldig." : "This confirmation link isn't valid.",
      body: isNl
        ? "De link is mogelijk verlopen of al gebruikt. Meld u opnieuw aan via de nieuwsbrief-vorm."
        : "The link may have expired or been used already. Please subscribe again via the newsletter form.",
    },
    error: {
      title: isNl ? "Er ging iets mis." : "Something went wrong.",
      body: isNl ? "Probeer het later opnieuw of neem contact op." : "Please try again later or reach out to us.",
    },
  };
  const c = copy[state.status] || copy.loading;

  return (
    <>
      <SEO title={isNl ? "Nieuwsbrief bevestigen" : "Confirm newsletter"} path="/newsletter/confirm" />
      <section className="relative overflow-hidden py-32 lg:py-44" data-testid="newsletter-confirm">
        <AmbientOrbs variant="gold" />
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <Eyebrow>{isNl ? "Nieuwsbrief" : "Newsletter"}</Eyebrow>
          <div className="mt-10 flex justify-center">
            {state.status === "loading" && <CircleNotch size={42} weight="thin" className="animate-spin text-bht-gold" />}
            {(state.status === "confirmed" || state.status === "already_confirmed") && (
              <CheckCircle size={54} weight="thin" className="text-bht-gold" />
            )}
            {(state.status === "not_found" || state.status === "error") && (
              <XCircle size={54} weight="thin" className="text-white/60" />
            )}
          </div>
          <motion.h1
            key={state.status}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 font-serif text-4xl leading-tight tracking-tight sm:text-5xl"
            data-testid="newsletter-confirm-title"
          >
            {c.title}
          </motion.h1>
          <p className="mt-6 text-base text-white/65">{c.body}</p>
          {state.email && (state.status === "confirmed" || state.status === "already_confirmed") && (
            <p className="mt-3 font-mono text-xs uppercase tracking-widest text-bht-gold/80">{state.email}</p>
          )}
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <GoldButton to={`/${lang}`}>{isNl ? "Terug naar home" : "Back to home"}</GoldButton>
            <GhostButton to={`/${lang}/knowledge`}>{isNl ? "Lees Knowledge Center" : "Read Knowledge Center"} →</GhostButton>
          </div>
        </div>
      </section>
    </>
  );
}
